/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2019, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

import {
  Component, Inject, Input, OnInit,
} from '@angular/core';
import { AnalyticsService } from '../../common/analytics.service';

import { FamilyMemberBaseContainerComponent as BaseContainer } from
  '../family-member-base-container/family-member-base-container.component';
import { FamilyMemberService } from '../family-member.service';
import FamilyMember from '../family-member.model';

/**
 * Component container that encapsulates the activity feed, and will hose the members activities.
 */
@Component({
  selector: 'bulb-familymember-activity-feed',
  templateUrl: './family-member-activity-feed.component.html',
  providers: [FamilyMemberService],
  standalone: false,
})
export class FamilyMemberActivityFeedComponent extends BaseContainer implements OnInit {
  private pageSize = 5;

  private lastPage = 0;

  // Input bound DOM element that references the family-members, associated members of the parent / guardian
  @Input() familyMembers: Array<FamilyMember>;

  // Input bound DOM element that references the parent / guardian:
  @Input() parent: any;

  public activities: Array<any>;

  private nextActivities: Array<any>;

  public showStudent: any;

  public studentHasNoActivities: boolean = false;

  public displayFamilyFilterButtons: boolean = true;

  public allFamilyBtns: Array<any>;

  public buttonActiveObj = { isButtonActive: false };

  public famMemberCombinedObj: any;

  public activitiesToDisplay: boolean = false;

  public familyMembersToDisplay: boolean = false;

  public newActivitiesExist: boolean = false;

  /**
   * Default Constructor.
   *
   * @param FamilyMemberService
   * @param analyticsService
   * @param BulbAuthService
   */
  constructor(
    private familyMemberService: FamilyMemberService,
    private analyticsService : AnalyticsService,
    @Inject('BulbAuthService') private BulbAuthService,
  ) {
    super();
  }

  ngOnInit(): void {
    // Fetch member activities for all connected students (null means all)
    this.showFeed(null);
  }

  onScroll(currentActivitiesArr) {
    if (currentActivitiesArr && currentActivitiesArr.length > 0) {
      const lastActivityId = currentActivitiesArr[currentActivitiesArr.length - 1];
      this.familyMemberService.fetchStudentActivities(this.parent.id, this.showStudent, lastActivityId.id)
        .then((response) => {
          this.nextActivities = response;
          this.activities = this.activities.concat(this.nextActivities);
        });
    }
  }

  showFeed(id) {
    if (id === null) { // if no family member is passed in, show all activities
      this.showStudent = undefined;

      this.familyMembers.forEach((member) => {
        member.setButtonActive(false);
      });

      this.familyMemberService.fetchStudentActivities(this.parent.id, this.showStudent)
        .then((response) => {
          this.activities = response;
        });
    } else { // one family member has been selected
      this.showStudent = id.getMemberId;
      id.setButtonActive(true);

      this.familyMembers.forEach((member) => { // set all other buttons to inactive
        if (this.showStudent !== member.getMemberId) {
          member.setButtonActive(false);
        }
      });
    }
    this.displayFamilyFilterButtons = true;
    this.familyMemberService.fetchStudentActivities(this.parent.id, this.showStudent)
      .then((response) => {
        this.activities = response;

        // if activities exist
        if (this.activities.length > 0) {
          this.studentHasNoActivities = false;
        }
        // if there is only one student - or none - hide the filter buttons
        if (this.familyMembers.length <= 1) {
          this.displayFamilyFilterButtons = false;
        }
        // if there are no activities we need to handle whether or not there are
        // no activities at all, or no activities for a particular student
        if (this.activities.length < 1) {
          this.studentHasNoActivities = false;
          // this handles whether or not the view is only of a particular student
          if (id && id.getMemberId) {
            this.studentHasNoActivities = true;
          }
        }

        // determining what to display in the activity feed
        if (this.activities.length > 0) {
          this.activitiesToDisplay = true;
        }
      });

    // determining what to display in the activity feed
    if (this.familyMembers.length > 0) {
      this.familyMembersToDisplay = true;
    }
  }

  scrollToAnchor(id) {
    this.familyMemberService.scrollToAnchor(id);
  }

  /**
   * Set the current 'state of the activity.
   *
   * @param activity Activity we want to toggle if it is a new one or not.
   */
  setNew(activity) {
    this.familyMemberService.setNewToFalse(this.parent.id, activity.id)
      .then(() => {
        this.familyMemberService.fetchStudentActivities(this.parent.id, this.showStudent)
          .then((response) => {
            // Send event from this angular component, to the angularJS controller that also needs it :
            const setNewFamilyAccountActivity = new CustomEvent('setNewFamilyAccountActivity', {
              detail: {
                setNewActivity: true,
                activities: response,
              },
            });
            // This updates the orange dot on the Student Activity tab
            window.dispatchEvent(setNewFamilyAccountActivity);

            this.activities = response;
            this.familyMemberService.setNewActivitiesToTrue(this.activities);
            this.newActivitiesExist = this.familyMemberService.newActivitiesExist;
            this.familyMemberService.refreshAccount();
          });
      });
  }

  /**
   * Capture information regarding FA activity usage.
   *
   * @param activityType Type of Activity the user clicked.
   */
  analyticsEvent(activityType : String) : void {
    /**
     * Activity Types can be :
     *
     * PAGE_PUBLISHED
     * PAGE_CREATED
     * COLLECTION_CREATED
     * FAMILY_MEMBER_CONNECTED
     * CHILD_DISCONNECTS
     */
    const activityFeedEventObject = {
      event: 'FamilyAccountActivityFeedClick',
      activityType,
      userId: this.BulbAuthService.currentUser.id,
    };
    this.analyticsService.pushEvent(activityFeedEventObject);
  }
}
